// @flow

import { graphql } from "gatsby";
import React, { useEffect, useState, useRef } from "react";
import Render from "../components/Contentful";
import { PageContent, CollectionSearch } from "@containers";
import {
    Meta,
    PropertyFilter,
    PropertyOverviewBreadcrumb,
    PropertyOverviewSort,
    PropertySearchMap,
    PropertySearchResults,
} from "@components";
import { useLocale, addLocale } from "ttag";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import {
    type SortEvent,
    EVENT_MAP,
    type FilterUpdateEvent,
    type PageImpressionParams,
} from "../models/events";
import { TrackPageImpression, PushEvent } from "../components/Tracker/Tracker";
import {
    PropertyOverviewSearchFactory,
    mapPropertiesForSearch,
    type Search,
    type PropertyOverviewFilter,
} from "@utils";

const CollectionTemplate = ({
    data: {
        contentfulCollection: { pageContent, pageTitle, pageDescription },
        contentfulNavigation: { navigationItems },
        topNavigationItems,
        allContentfulBlog: { nodes: blogs },
        c21Translation: { translations },
        footerItems,
        socialLinks,
    },
    pageContext: { locale, translationSlugs, country, predefinedFilter },
}: *) => {
    addLocale(locale, translations);
    useLocale(locale);
    const sendToGTM = useGTMDispatch();
    const [propertyOverviewSearch: Search, setPropertyOverviewSearch] =
        useState(
            PropertyOverviewSearchFactory.fromInitialValues({
                filter: predefinedFilter,
            }),
        );
    const [searchParams, setSearchParams] = useState({
        filter: predefinedFilter,
    });
    const [previousFilterQs, setPreviousFilter] = useState("");
    const [properties, setProperties] = useState([]);
    const [qs, setQs] = useState();
    const [metaData, setMetaData] = useState({
        pageCount: 0,
        totalCount: 0,
        facets: undefined,
    });
    const [numberOfResults, setNumberOfResults] = useState();
    const [isLoading, setIsLoading] = useState(true);

    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;

        const paramsString = location.search;
        let searchParams = new URLSearchParams(paramsString);

        const pageParams: PageImpressionParams = {
            agency: searchParams.get("agency")
                ? {
                      id: searchParams.get("agency") || "",
                      name: "",
                      city: "",
                      postalcode: "",
                      country: "",
                  }
                : null,
            agencypage: "false",
            language: locale,
            pagename: "Collection Search",
            typetemplate: "collection",
        };
        TrackPageImpression(sendToGTM, EVENT_MAP.PageImpression, pageParams);

        return () => {
            isMounted.current = false;
        };
    }, []);

    const onFilterChanged = (filter: PropertyOverviewFilter) => {
        if (filter.location && filter.location === null) {
            delete filter.location;
        }

        // Join the filter with the predefined filter
        const combinedFilter = { ...predefinedFilter, ...filter };

        setPreviousFilter(location.search);
        propertyOverviewSearch.setFilter(combinedFilter);
        applySearch();
    };

    const onFilterCleared = () => {
        setPropertyOverviewSearch(
            PropertyOverviewSearchFactory.fromInitialValues({
                filter: predefinedFilter,
                sort: searchParams?.sort,
            }),
        );
        setSearchParams({ filter: predefinedFilter });
        applySearch();
    };

    const onSortChanged = (sort) => {
        const event: SortEvent = {
            order: sort.direction,
            field: sort.field,
            type: EVENT_MAP.SortEvent,
        };
        PushEvent(event, sendToGTM);
        propertyOverviewSearch.setSort(sort);
        applySearch();
    };

    const onPageChanged = (page) => {
        propertyOverviewSearch.setPage(page);
        applySearch();
    };

    const applySearch = () => {
        setIsLoading(true);
        setSearchParams(propertyOverviewSearch.current);
        propertyOverviewSearch
            .search()
            .then((result) => {
                if (isMounted.current) {
                    setMetaData(result.data._meta);
                    setNumberOfResults(result.data._meta.totalCount);
                    return mapPropertiesForSearch(result.data.data, locale);
                }
            })
            .then((properties) => {
                if (isMounted.current) {
                    setProperties(properties);
                    setSearchParams(propertyOverviewSearch.current); // re-apply boundaries on search params with new results
                    setIsLoading(false);
                }
            });

        setQs(propertyOverviewSearch?.toBrowserQueryString());
    };

    const handleFilterEvent = () => {
        if (previousFilterQs === location.search) {
            return;
        }

        const filtersParams = new URLSearchParams(location.search);
        const previousFiltersParams = new URLSearchParams(previousFilterQs);

        const filterCurrent = [];
        const filterPrevious = [];

        for (let entry of filtersParams.entries()) {
            filterCurrent.push(entry);
        }

        for (let entry of previousFiltersParams.entries()) {
            filterPrevious.push(entry);
        }

        const filterUpdateEvent: FilterUpdateEvent = {
            fieldname: "",
            fieldtype: "",
            value: "",
            action: "",
            selection: filterCurrent,
            type: EVENT_MAP.FilterUpdateEvent,
        };

        if (filterPrevious.length > 0 || filterCurrent.length > 0) {
            if (filterPrevious.length > filterCurrent.length) {
                let i = 0;
                if (filterCurrent.length > 0) {
                    while (
                        filterCurrent[i] &&
                        filterCurrent[i][0] === filterPrevious[i][0]
                    ) {
                        i++;
                    }
                }

                filterUpdateEvent.fieldname = filterPrevious[i][0];
                filterUpdateEvent.fieldtype =
                    filterPrevious[i][1] === "true" ||
                    filterPrevious[i][1] === "false"
                        ? "bullet"
                        : "select";
                filterUpdateEvent.value = filterPrevious[i][1];
                filterUpdateEvent.action = "remove";
            } else {
                let i = 0;

                if (filterCurrent.length > 0 && filterPrevious.length > 0) {
                    while (
                        filterPrevious[i] &&
                        filterPrevious[i][0] === filterCurrent[i][0] &&
                        filterPrevious[i] &&
                        filterPrevious[i][1] === filterCurrent[i][1]
                    ) {
                        i++;
                    }
                }

                filterUpdateEvent.fieldname = filterCurrent[i][0];
                filterUpdateEvent.fieldtype =
                    filterCurrent[i][1] === "true" ||
                    filterCurrent[i][1] === "false"
                        ? "bullet"
                        : "select";
                filterUpdateEvent.value = filterCurrent[i][1];
                filterUpdateEvent.action = "apply";
            }
            PushEvent(filterUpdateEvent, sendToGTM);
        }
    };

    useEffect(() => {
        if (!propertyOverviewSearch) return;

        applySearch();
    }, [propertyOverviewSearch]);

    useEffect(() => {
        if (!propertyOverviewSearch) return; // prevent flicker of qs on page load

        handleFilterEvent();
    }, [qs]);

    const optionsWithQuery = {};

    Object.keys(translationSlugs).forEach(
        (key) =>
            (optionsWithQuery[key] = `${translationSlugs[key]}${
                propertyOverviewSearch?.toBrowserQueryString() || ""
            }`),
    );

    const filter = (
        <PropertyFilter
            onFilterChanged={onFilterChanged}
            onFilterCleared={onFilterCleared}
            currentFilter={searchParams?.filter}
            facets={metaData.facets}
            language={locale}
            filterType={"propertyFilter"}
            countryFilterWhitelist={
                process.env.GATSBY_ALLOWED_COUNTRIES?.split(",") || []
            }
        />
    );

    const map = <PropertySearchMap properties={properties} locale={locale} />;

    const sort = (
        <PropertyOverviewSort
            numberOfResults={numberOfResults}
            currentSort={searchParams?.sort}
            onSortChanged={onSortChanged}
        />
    );

    const results = (
        <PropertySearchResults
            properties={properties}
            numberOfResults={numberOfResults}
            currentPage={searchParams?.page}
            onPageChanged={onPageChanged}
            Header={sort}
        />
    );

    return (
        <PageContent
            locale={locale}
            navigationItems={navigationItems}
            translationSlugs={translationSlugs}
            footerItems={footerItems}
            topNavigationItems={topNavigationItems?.navigationItems}
            country={country}
            socialLinks={socialLinks}
            Breadcrumb={<PropertyOverviewBreadcrumb locale={locale} />}
        >
            <Meta
                title={pageTitle}
                description={pageDescription?.pageDescription}
                translationSlugs={translationSlugs}
                locale={locale}
            />
            <CollectionSearch
                Filter={filter}
                Map={map}
                Results={results}
                pageTitle={pageTitle}
                isLoading={isLoading}
            />
            <>{Render(pageContent, locale, blogs)}</>
        </PageContent>
    );
};

export default CollectionTemplate;

export const query = graphql`
    query ($locale: String!, $id: String!, $country: String!) {
        contentfulNavigation(
            title: { eq: "Main Navigation" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        topNavigationItems: contentfulNavigation(
            title: { eq: "Main navigation bar" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        footerItems: contentfulNavigation(
            title: { eq: "Footer" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        socialLinks: allContentfulLink(
            filter: {
                metadata: { tags: { elemMatch: { name: { eq: "Socials" } } } }
                node_locale: { eq: $locale }
            }
        ) {
            nodes {
                linkTitle
                externalLink
            }
        }
        c21Translation(locale: { eq: $locale }) {
            translations(
                keys: [
                    "common.text.language"
                    "common.text.follow-us"
                    "common.filters.*"
                    "property-overview.*"
                    "api.*"
                    "slug.property-detail"
                    "common.text.yes"
                    "common.text.no"
                    "common.text.maybe"
                    "common.price.on-demand"
                    "map.*"
                    "title.property-overview"
                    "meta.description.property-overview"
                    "breadcrumbs.property-overview.*"
                    "agency.header.button.text"
                    "slug.estimation-tool"
                    "label.estimation-tool"
                    "slug.privacy-policy"
                    "common.text.search"
                    "home.category-links"
                    "landing.blog.article"
                    "landing.step-block.step"
                    "form.agency-contact.*"
                    "form.hq-contact.*"
                    "search.noresults.title"
                    "search.noresults.description"
                    "search"
                ]
            )
        }
        contentfulCollection(id: { eq: $id }, node_locale: { eq: $locale }) {
            __typename
            contentful_id
            slug
            pageTitle
            pageDescription {
                pageDescription
            }
            country
            content {
                contentTitle
                squareImage {
                    gatsbyImageData(width: 217, height: 185, layout: FIXED)
                    description
                }
                landscapeImage {
                    gatsbyImageData
                    description
                }
            }
            pageContent {
                ... on ContentfulQuickSearchBlock {
                    __typename
                    id
                    node_locale
                    background {
                        size
                        color
                        position
                    }
                    content {
                        contentTitle
                        contentSubtitle
                        squareImage {
                            gatsbyImageData(width: 2000, quality: 80)
                            description
                        }
                    }
                    filters
                    quickLinks {
                        type
                        __typename
                        linkItems {
                            ... on ContentfulLink {
                                __typename
                                id
                                linkTitle
                                icon {
                                    gatsbyImageData(
                                        width: 100
                                        height: 100
                                        layout: FIXED
                                    )
                                }
                                externalLink
                                contentfulInternalLink {
                                    ... on ContentfulWebLandingPage {
                                        __typename
                                        slug
                                    }
                                }
                            }
                        }
                    }
                    overlaySelectedBrand {
                        gatsbyImageData(width: 2000, quality: 80)
                        description
                    }
                }
                ... on ContentfulBanner {
                    __typename
                    id
                    imageLink {
                        ... on ContentfulLink {
                            id
                            linkTitle
                            externalLink
                            contentfulInternalLink {
                                ... on ContentfulWebLandingPage {
                                    slug
                                }
                            }
                        }
                    }
                    desktopImage {
                        __typename
                        gatsbyImageData(width: 2344, quality: 80)
                        title
                        description
                    }
                    mobileImage {
                        __typename
                        gatsbyImageData(width: 2344, quality: 80)
                        title
                        description
                    }
                }
                ... on ContentfulQuickLinkBlock {
                    __typename
                    id
                    type
                    title
                    blogTag
                    hideStepsTitle
                    background {
                        size
                        color
                        position
                    }
                    linkItems {
                        ... on ContentfulLink {
                            __typename
                            id
                            contentfulInternalLink {
                                ... on ContentfulWebLandingPage {
                                    __typename
                                    slug
                                }
                            }
                            externalLink
                            linkTitle
                            linkSubtitle {
                                linkSubtitle
                            }
                            icon {
                                gatsbyImageData(
                                    width: 100
                                    height: 100
                                    layout: FIXED
                                )
                            }
                            image {
                                gatsbyImageData(
                                    width: 576
                                    height: 450
                                    layout: CONSTRAINED
                                )
                                description
                            }
                        }
                        ... on ContentfulCollection {
                            __typename
                            internalLabel
                            query
                            slug
                            pageTitle
                            content {
                                contentTitle
                                squareImage {
                                    gatsbyImageData(
                                        width: 576
                                        height: 450
                                        layout: CONSTRAINED
                                    )
                                    description
                                }
                            }
                        }
                        ... on ContentfulWebLandingPage {
                            __typename
                            id
                            slug
                            pageContent {
                                ... on ContentfulBlog {
                                    __typename
                                    id
                                    title
                                    heroImage {
                                        gatsbyImageData(width: 568, quality: 80)
                                        description
                                    }
                                }
                            }
                        }
                    }
                }
                ... on ContentfulImageInformationBlock {
                    __typename
                    id
                    node_locale
                    type
                    background {
                        size
                        color
                        position
                    }
                    backgroundColor
                    imagePosition
                    textColor
                    maxHeight
                    link {
                        linkTitle
                        externalLink
                        contentfulInternalLink {
                            ... on ContentfulWebLandingPage {
                                __typename
                                slug
                            }
                            ... on ContentfulCollection {
                                __typename
                                query
                            }
                        }
                        fileLink {
                            file {
                                url
                            }
                        }
                    }
                    content {
                        contentTitle
                        contentSubtitle
                        landscapeImage {
                            gatsbyImageData(width: 2344, quality: 80)
                            description
                        }
                        squareImage {
                            gatsbyImageData(width: 1800, quality: 80)
                            description
                        }
                    }
                }
                ... on ContentfulBlog {
                    __typename
                    id
                    title
                    author {
                        name
                        link
                        image {
                            gatsbyImageData(
                                width: 100
                                height: 100
                                layout: FIXED
                            )
                        }
                    }
                    content {
                        ... on ContentfulRichText {
                            __typename
                            text {
                                raw
                            }
                            internal {
                                type
                            }
                        }
                    }
                    heroImage {
                        __typename
                        gatsbyImageData(width: 1172)
                        description
                    }
                }
                ... on ContentfulRichText {
                    __typename
                    text {
                        raw
                    }
                    internal {
                        type
                    }
                }
                ... on ContentfulUspBlock {
                    __typename
                    id
                    type
                    uspItems {
                        icon {
                            gatsbyImageData(
                                width: 100
                                height: 100
                                layout: FIXED
                            )
                        }
                        linkTitle
                        linkSubtitle {
                            linkSubtitle
                        }
                    }
                    title
                }
                ... on ContentfulContactHqBlock {
                    __typename
                    id
                    internalLabel
                    formType
                    internal {
                        type
                    }
                }
                ... on ContentfulContainer {
                    __typename
                    title
                    faq: content {
                        __typename
                        ... on ContentfulFaq {
                            question
                            answer {
                                answer
                            }
                        }
                    }
                }
                ... on ContentfulNearbyPlaces {
                    __typename
                    id
                    internalName
                    postalCode
                    findNearbyLabel
                    places {
                        id
                        pageTitle
                        slug
                    }
                }
            }
        }
        allContentfulBlog(filter: { node_locale: { eq: $locale } }) {
            nodes {
                __typename
                id
                title
                tags
                author {
                    name
                    link
                    image {
                        gatsbyImageData(width: 100, height: 100, layout: FIXED)
                    }
                }
                content {
                    ... on ContentfulRichText {
                        __typename
                        text {
                            raw
                        }
                        internal {
                            type
                        }
                    }
                }
                heroImage {
                    __typename
                    gatsbyImageData(width: 1172)
                    description
                }
            }
        }
    }
`;
